import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useGameData } from "./GameContext";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels"; // Datalabels eklentisini ekliyoruz
import { gameDetails } from "../App";
import logo from "../assets/new-fire-score-logo.png";
import { useLanguage } from "../context/LanguageContext";
import Navbar from "../components/Navbar";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const USERS_PER_PAGE = 10; // Her sayfada 10 kullanıcı

const UsersPage: React.FC = () => {
    const { gameKey } = useParams<{ gameKey: string }>();
    const navigate = useNavigate();
    const { gameData, refreshGameData, lastRefreshed, isLoading } = useGameData();
    const { language, setLanguage, t } = useLanguage();
    const sessions = gameData[gameKey || ""] || [];
    
    // Debug logging
    console.log('Current gameKey:', gameKey);
    console.log('Sessions for this game:', sessions);
    
    const [currentPage, setCurrentPage] = useState(1);
    const [userIdFilter, setUserIdFilter] = useState<string>("");
    const [purchaseFilter, setPurchaseFilter] = useState<boolean | null>(null);
    const [confirmFilter, setConfirmFilter] = useState<boolean | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleBack = () => {
        navigate("/");
    };

    const handleRefresh = async () => {
        if (gameKey) {
            await refreshGameData(gameKey);
        }
    };

    // Download user data as JSON
    const handleDownloadJSON = () => {
        // Use filtered sessions data or all sessions if no filters applied
        const dataToDownload = filteredSessions.map(session => {
            // Create a copy of the session object
            const cleanedSession = {...session};
            
            // Clean any nested JSON strings by parsing and re-stringifying them
            if (cleanedSession.horoscopeData && typeof cleanedSession.horoscopeData === 'string') {
                try {
                    // Parse and re-stringify to remove any formatted JSON
                    const parsedData = JSON.parse(cleanedSession.horoscopeData);
                    cleanedSession.horoscopeData = JSON.stringify(parsedData);
                } catch (e) {
                    // Keep original if it's not valid JSON
                }
            }
            
            if (cleanedSession.HoroscopeInfo && typeof cleanedSession.HoroscopeInfo === 'string') {
                try {
                    const parsedInfo = JSON.parse(cleanedSession.HoroscopeInfo);
                    cleanedSession.HoroscopeInfo = JSON.stringify(parsedInfo);
                } catch (e) {
                    // Keep original if it's not valid JSON
                }
            }
            
            if (cleanedSession.HoroscopeList && typeof cleanedSession.HoroscopeList === 'string') {
                try {
                    const parsedList = JSON.parse(cleanedSession.HoroscopeList);
                    cleanedSession.HoroscopeList = JSON.stringify(parsedList);
                } catch (e) {
                    // Keep original if it's not valid JSON
                }
            }
            
            return cleanedSession;
        });
        
        // Create a blob with the data - use compact JSON format without newlines
        const fileData = JSON.stringify(dataToDownload);
        const blob = new Blob([fileData], { type: "application/json" });
        
        // Create a download link and trigger click
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const gameName = gameKey ? gameDetails[gameKey]?.name : "game";
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        
        link.download = `${gameName}_users_${timestamp}.json`;
        link.href = url;
        link.click();
        
        // Clean up
        URL.revokeObjectURL(url);
    };

    // Saniyeyi saat, dakika, saniye formatına çevirme
    const formatSessionTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        let result = "";
        if (hours > 0) result += `${hours}${t("h")} `;
        if (minutes > 0) result += `${minutes}${t("m")} `;
        if (remainingSeconds > 0 || result === "") result += `${remainingSeconds}${t("s")}`;
        return result.trim();
    };
    // Mevcut state ve logic aynı kalacak...
    const [hoveredUserId, setHoveredUserId] = useState<string | null>(null);

    // Filtrelenmiş Oturumlar
    const filteredSessions = sessions.filter((session) => {
        const matches = (
            (!userIdFilter || session.userId.toLowerCase().includes(userIdFilter.toLowerCase())) &&
            (purchaseFilter === null || session.purchaseClicked === purchaseFilter) &&
            (confirmFilter === null || session.confirmClicked === confirmFilter)
        );
        console.log('Session:', session.userId, 'Matches filter:', matches); // Debug log
        return matches;
    });

    // Genel İstatistikler (Filtrelenmiş Veriler Üzerinden)
    const totalUsers = filteredSessions.length;
    const avgSessionTime =
        totalUsers > 0 ? filteredSessions.reduce((sum, s) => sum + s.sessionTime, 0) / totalUsers : 0;

    // Pagination Hesaplamaları
    const totalPages = Math.ceil(totalUsers / USERS_PER_PAGE);
    const startIndex = (currentPage - 1) * USERS_PER_PAGE;
    const endIndex = startIndex + USERS_PER_PAGE;
    const paginatedSessions = filteredSessions.slice(startIndex, endIndex);

    // Purchase Clicked Grafik Verisi
    const purchaseData = {
        labels: [t("purchased"), t("not_purchased")],
        datasets: [
            {
                label: t("users"),
                data: [
                    filteredSessions.filter((s) => s.purchaseClicked).length,
                    filteredSessions.filter((s) => !s.purchaseClicked).length,
                ],
                backgroundColor: ["rgba(75, 192, 192, 0.7)", "rgba(255, 99, 132, 0.7)"],
                borderColor: ["rgba(75, 192, 192, 1)", "rgba(255, 99, 132, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    // Confirm Clicked Grafik Verisi
    const confirmData = {
        labels: [t("confirmed"), t("not_confirmed")],
        datasets: [
            {
                label: t("users"),
                data: [
                    filteredSessions.filter((s) => s.confirmClicked).length,
                    filteredSessions.filter((s) => !s.confirmClicked).length,
                ],
                backgroundColor: ["rgba(54, 162, 235, 0.7)", "rgba(255, 159, 64, 0.7)"],
                borderColor: ["rgba(54, 162, 235, 1)", "rgba(255, 159, 64, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    // Genel İstatistik Grafiği (avgSessionTime saniye cinsinden ham veri olarak kalacak)
    const statsData = {
        labels: [t("total_users"), t("avg_session")], // "avg_session_min" yerine "avg_session" yaptım
        datasets: [
            {
                label: t("stats"),
                data: [totalUsers, avgSessionTime], // Ham saniye değerini kullanıyoruz
                backgroundColor: ["rgba(153, 102, 255, 0.7)", "rgba(255, 206, 86, 0.7)"],
                borderColor: ["rgba(153, 102, 255, 1)", "rgba(255, 206, 86, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "top" as const, labels: { font: { size: 12 } } },
            title: {
                display: true,
                font: { size: 16, weight: "600" as const },
                color: "#1f2937",
            },
            tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleFont: { size: 14 },
                bodyFont: { size: 12 },
                callbacks: {
                    label: (context: any) => {
                        const label = context.label || "";
                        const value = context.raw;
                        if (label === t("avg_session")) {
                            return `${label}: ${formatSessionTime(value)}`;
                        }
                        return `${label}: ${value}`;
                    },
                },
            },
            datalabels: {
                color: "#1f2937",
                font: { weight: 600, size: 12 },
                formatter: (value: number, context: any) => {
                    if (context.chart.data.labels![context.dataIndex] === t("avg_session")) {
                        return formatSessionTime(value);
                    }
                    return value;
                },
                anchor: "end" as const,
                align: "top" as const,
            },
        },
        scales: {
            y: { beginAtZero: true, grid: { color: "rgba(0, 0, 0, 0.05)" } },
            x: { grid: { display: false } },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
    };

    const gameName = gameKey ? gameDetails[gameKey]?.name : t("game");

    // Pagination Kontrolleri
    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Navbar />
            <main className="mt-36 mb-16 flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8 flex items-center justify-between"
                >
                    <div className="flex items-center space-x-4">
                        <img
                            src={gameDetails[gameKey || ""]?.logo || ""}
                            alt={gameName}
                            className="w-12 h-12 rounded-full"
                        />
                        <h2 className="text-3xl font-bold text-gray-800">{gameName} {t("users")}</h2>
                    </div>
                    <div className="flex items-center">
                        {gameKey && lastRefreshed[gameKey] && (
                            <span className="text-sm text-gray-500 mr-3">
                                {t("last_updated")}: {lastRefreshed[gameKey]?.toLocaleTimeString()}
                            </span>
                        )}
                        <button
                            onClick={handleRefresh}
                            disabled={gameKey ? isLoading[gameKey] : false}
                            className={`px-4 py-2 rounded-lg text-white font-medium ${
                                gameKey && isLoading[gameKey]
                                ? "bg-gray-400 cursor-not-allowed" 
                                : "bg-blue-500 hover:bg-blue-600"
                            } mr-3`}
                        >
                            {gameKey && isLoading[gameKey] ? t("refreshing") : t("refresh_data")}
                        </button>
                        <button
                            onClick={handleBack}
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors mr-3"
                        >
                            {t("back")}
                        </button>
                        <button
                            onClick={handleDownloadJSON}
                            disabled={gameKey ? isLoading[gameKey] || filteredSessions.length === 0 : false}
                            className={`px-4 py-2 rounded-lg text-white font-medium ${
                                gameKey && isLoading[gameKey] || filteredSessions.length === 0
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-green-500 hover:bg-green-600"
                            }`}
                        >
                            {t("download_json") || "Download JSON"}
                        </button>
                    </div>
                </motion.div>

                {gameKey && isLoading[gameKey] ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                            <p className="text-gray-600">{t("loading_data_please_wait")}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Filtreleme Bölümü */}
                        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-semibold text-gray-800">{t("filters")}</h3>
                                <div className="text-sm text-gray-500">
                                    {t("total_filtered_users") || "Total users"}: {filteredSessions.length}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("user_id")}</label>
                                    <input
                                        type="text"
                                        value={userIdFilter}
                                        onChange={(e) => setUserIdFilter(e.target.value)}
                                        placeholder={t("eg_user_id")}
                                        className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("purchase_status")}</label>
                                    <select
                                        value={purchaseFilter === null ? "" : purchaseFilter ? "true" : "false"}
                                        onChange={(e) => setPurchaseFilter(e.target.value === "" ? null : e.target.value === "true")}
                                        className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">{t("all")}</option>
                                        <option value="true">{t("purchased")}</option>
                                        <option value="false">{t("not_purchased")}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("confirm_status")}</label>
                                    <select
                                        value={confirmFilter === null ? "" : confirmFilter ? "true" : "false"}
                                        onChange={(e) => setConfirmFilter(e.target.value === "" ? null : e.target.value === "true")}
                                        className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">{t("all")}</option>
                                        <option value="true">{t("confirmed")}</option>
                                        <option value="false">{t("not_confirmed")}</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Grafik Bölümü */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="bg-white rounded-xl shadow-lg p-5"
                            >
                                <Bar
                                    data={purchaseData}
                                    options={{
                                        ...chartOptions,
                                        plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("purchase_stats"), font: { size: 16, weight: 600 } } },
                                    }}
                                    height={200}
                                />
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="bg-white rounded-xl shadow-lg p-5"
                            >
                                <Bar
                                    data={confirmData}
                                    options={{
                                        ...chartOptions,
                                        plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("confirm_stats"), font: { size: 16, weight: 600 } } },
                                    }}
                                    height={200}
                                />
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="bg-white rounded-xl shadow-lg p-5"
                            >
                                <Bar
                                    data={statsData}
                                    options={{
                                        ...chartOptions,
                                        plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("general_stats"), font: { size: 16, weight: 600 } } },
                                    }}
                                    height={200}
                                />
                            </motion.div>
                        </div>

                        {/* Kullanıcı Kartları */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {paginatedSessions.length > 0 ? (
                                paginatedSessions.map((session, index) => (
                                    <motion.div
                                        key={session.userId}
                                        variants={cardVariants}
                                        initial="hidden"
                                        animate="visible"
                                        transition={{ delay: index * 0.05 }}
                                        className="bg-white rounded-xl shadow-lg p-5 hover:shadow-xl transition-shadow duration-200 relative"
                                        onMouseEnter={() => setHoveredUserId(session.userId)}
                                        onMouseLeave={() => setHoveredUserId(null)}
                                    >
                                        <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                            {t("user")} {/* ID burada doğrudan gösterilmiyor */}
                                        </h3>
                                        <div className="space-y-2 text-gray-600">
                                            <p>
                                                <span className="font-medium text-blue-600">{t("session_time")}:</span>{" "}
                                                {formatSessionTime(session.sessionTime)}
                                            </p>
                                            <p>
                                                <span className="font-medium text-red-600">{t("max_level")}:</span> {session.maxLevel}
                                            </p>
                                            <p>
                                                <span className="font-medium text-green-600">{t("confirm_clicked")}:</span>{" "}
                                                {session.confirmClicked ? t("yes") : t("no")}
                                            </p>
                                            <p>
                                                <span className="font-medium text-purple-600">{t("purchase_clicked")}:</span>{" "}
                                                {session.purchaseClicked ? t("yes") : t("no")}
                                            </p>
                                        </div>

                                        {/* Tooltip */}
                                        {hoveredUserId === session.userId && (
                                            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-sm rounded-lg px-3 py-1 shadow-lg transition-opacity duration-200">
                                                {t("user")} #{session.userId}
                                            </div>
                                        )}
                                    </motion.div>
                                ))
                            ) : (
                                <p className="col-span-full text-gray-500 text-center py-8">
                                    {t("no_user_data", { gameName })}
                                </p>
                            )}
                        </div>

                        {/* Pagination Kontrolleri */}
                        {totalPages > 1 && (
                            <div className="mt-8 flex items-center justify-center space-x-4">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`py-2 px-4 rounded-lg font-medium transition-all duration-200 ${currentPage === 1
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : "bg-blue-500 text-white hover:bg-blue-600"
                                        }`}
                                >
                                    {t("previous")}
                                </motion.button>
                                <span className="text-gray-700">
                                    {t("page")} {currentPage} / {totalPages}
                                </span>
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`py-2 px-4 rounded-lg font-medium transition-all duration-200 ${currentPage === totalPages
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : "bg-blue-500 text-white hover:bg-blue-600"
                                        }`}
                                >
                                    {t("next")}
                                </motion.button>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Footer */}
            <footer className="bg-gray-800 py-4">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <p className="text-gray-300 text-sm">{t("footer", { year: new Date().getFullYear() })}</p>
                </div>
            </footer>
        </div>
    );
};

export default UsersPage;